import { typesenseClient, typesenseSearchClient } from './client';
import { SEARCH, PAGINATION } from '@/constants/app.constants';
import { GenreRecommendationService } from '../GenreRecommendationService';

export interface SearchParams {
  query: string;
  filters?: Record<string, any>;
  location?: { lat: number; lng: number };
  radius_km?: number;
  sort_by?: string;
  page?: number;
  per_page?: number;
  facet_by?: string[];
  include_facet_counts?: boolean;
  // Enhanced genre filtering options
  genreFilters?: {
    genreIds?: string[];
    genreSlugs?: string[];
    genreCategories?: string[];
    featuredGenres?: boolean;
    minGenreSequenceCount?: number;
    maxGenreSequenceCount?: number;
    minGenreTotalDownloads?: number;
    maxGenreTotalDownloads?: number;
    minGenreAverageRating?: number;
    maxGenreAverageRating?: number;
    genreMappingStatus?: string[];
  };
  // New convenience flags
  upvoteSort?: boolean;
  trendingSort?: boolean;
}

export class TypesenseSearchService {
  /**
   * Search sequences collection with enhanced genre filtering
   */
  static async searchSequences(params: SearchParams) {
    try {
      const searchParams: any = {
        q: params.query || '*',
        query_by: 'search_text',
        num_typos: 1,
        sort_by: params.sort_by || (params.trendingSort ? 'hot_score:desc,_text_match:desc' : params.upvoteSort ? 'upvote_count:desc,_text_match:desc' : SEARCH.DEFAULT_SORT),
        page: params.page || 1,
        per_page: params.per_page || PAGINATION.SEARCH_PAGE_SIZE,
        ...(params.facet_by && { facet_by: params.facet_by.join(',') }),
        include_facet_counts: params.include_facet_counts || true,
        // Ensure we get enough facet buckets so the UI can show counts for all holidays
        max_facet_values: 200,
      };

      // Add filters if provided
      if (params.filters && Object.keys(params.filters).length > 0) {
        console.log('[TypeSense SearchService] Applying filters:', params.filters);
        const filterStrings = [];

        // Enhanced field mapping including new genre fields
        const fieldMapping: Record<string, string> = {
          // UI -> Typesense schema name mappings
          software: 'software_type',
          genres: 'genres',
          genreIds: 'genre_id',
          genreSlugs: 'genre_slug',
          genreCategories: 'genre_category',
          featuredGenres: 'genre_featured',
          artists: 'artist',
          holidays: 'holiday_type', // Use standardized holidays field name in UI, maps to holiday_type in Typesense
          minPrice: 'price',
          maxPrice: 'price',
          isFree: 'is_free',
          // Additional facets/filters
          fileFormats: 'file_type',
          albums: 'album',
          // New genre-specific filters
          minGenreSequenceCount: 'genre_sequence_count',
          maxGenreSequenceCount: 'genre_sequence_count',
          minGenreTotalDownloads: 'genre_total_downloads',
          maxGenreTotalDownloads: 'genre_total_downloads',
          minGenreAverageRating: 'genre_average_rating',
          maxGenreAverageRating: 'genre_average_rating',
          genreMappingStatus: 'genre_mapping_status'
        };
        
        for (const [key, value] of Object.entries(params.filters)) {
          const fieldName = fieldMapping[key] || key;

          // Handle price ranges explicitly (Typesense supports range/comparators)
          if (key === 'minPrice' && typeof value === 'number') {
            filterStrings.push(`${fieldName}:>=${value}`);
            continue;
          }
          if (key === 'maxPrice' && typeof value === 'number') {
            filterStrings.push(`${fieldName}:<=${value}`);
            continue;
          }
          
          // Skip empty, null, undefined, or false values entirely
          if (value === undefined || value === null || value === '' || value === false) {
            continue;
          }
          
          if (Array.isArray(value)) {
            // Only add array filters that have actual non-empty values
            let nonEmptyValues = value.filter(v => v && typeof v === 'string' && v.trim() !== '');
            // Normalize case for specific fields
            if (fieldName === 'genres') {
              nonEmptyValues = nonEmptyValues.map(v => v.toLowerCase());
            }
            if (nonEmptyValues.length > 0) {
              filterStrings.push(`${fieldName}:[${nonEmptyValues.map(v => `"${v}"`).join(',')}]`);
            }
          } else if (typeof value === 'string') {
            // Only add non-empty string values
            if (value.trim() !== '') {
              filterStrings.push(`${fieldName}:"${value}"`);
            }
          } else if (typeof value === 'boolean' && value === true) {
            // Only add true boolean values
            filterStrings.push(`${fieldName}:${value}`);
          } else if (typeof value === 'number') {
            // Add numeric values
            filterStrings.push(`${fieldName}:${value}`);
          }
        }
        
        // Add enhanced genre filters if provided
        if (params.genreFilters) {
          const genreFilterStrings = this.buildGenreFilters(params.genreFilters);
          filterStrings.push(...genreFilterStrings);
        }

        if (filterStrings.length > 0) {
          searchParams.filter_by = filterStrings.join(' && ');
          console.log('[TypeSense SearchService] Generated filter string:', searchParams.filter_by);
        }
      }

      // Default facet set when none provided: use schema field names
      if (!searchParams['facet_by']) {
        searchParams['facet_by'] = 'genre_name,software_type,holiday_type,price,artist_name,album,file_type';
      }

      // Primary-genre boost: if filters.genres present, run a second query preferring primaryGenre
      const requestedGenres = Array.isArray(params.filters?.genres) ? params.filters!.genres : [];
      if (requestedGenres.length > 0) {
        const filterBase = searchParams.filter_by ? searchParams.filter_by + ' && ' : '';
        const primaryFilter = `${filterBase}primaryGenre:[${requestedGenres.map((g: string) => `"${g.toLowerCase()}"`).join(',')}]`;

        const [primaryRes, generalRes] = await Promise.all([
          typesenseSearchClient.collections('sequences_v2').documents().search({
            ...searchParams,
            page: 1,
            per_page: Math.min((params.per_page || PAGINATION.SEARCH_PAGE_SIZE), 50),
            filter_by: primaryFilter,
          }),
          typesenseSearchClient.collections('sequences_v2').documents().search(searchParams),
        ]);

        // Interleave results with medium boost: 1 primary for every 2 general
        const primaryHits = primaryRes.hits || [];
        const generalHits = (generalRes.hits || []).filter((gh: any) => !primaryHits.find((ph: any) => ph.document.id === gh.document.id));
        const merged: any[] = [];
        const ratio = 2; // medium boost
        let i = 0, j = 0;
        while (i < primaryHits.length || j < generalHits.length) {
          if (i < primaryHits.length) merged.push(primaryHits[i++]);
          for (let k = 0; k < ratio && j < generalHits.length; k++) merged.push(generalHits[j++]);
          if (merged.length >= (params.per_page || PAGINATION.SEARCH_PAGE_SIZE)) break;
        }

        return {
          ...generalRes,
          hits: merged,
          found: generalRes.found,
          facet_counts: generalRes.facet_counts,
        };
      }

      const result = await typesenseSearchClient.collections('sequences_v2').documents().search(searchParams);
      return result;
    } catch (error) {
      console.error('Sequences search failed:', error);
      throw new Error(`Sequences search failed: ${error.message}`);
    }
  }

  /**
   * Build genre-specific filter strings
   */
  private static buildGenreFilters(genreFilters: SearchParams['genreFilters']): string[] {
    const filterStrings: string[] = [];
    
    if (!genreFilters) return filterStrings;

    // Genre IDs filter
    if (genreFilters.genreIds && genreFilters.genreIds.length > 0) {
      filterStrings.push(`genre_id:[${genreFilters.genreIds.map(id => `"${id}"`).join(',')}]`);
    }

    // Genre slugs filter
    if (genreFilters.genreSlugs && genreFilters.genreSlugs.length > 0) {
      filterStrings.push(`genre_slug:[${genreFilters.genreSlugs.map(slug => `"${slug}"`).join(',')}]`);
    }

    // Genre categories filter
    if (genreFilters.genreCategories && genreFilters.genreCategories.length > 0) {
      filterStrings.push(`genre_category:[${genreFilters.genreCategories.map(cat => `"${cat}"`).join(',')}]`);
    }

    // Featured genres filter
    if (genreFilters.featuredGenres !== undefined) {
      filterStrings.push(`genre_featured:${genreFilters.featuredGenres}`);
    }

    // Genre sequence count range
    if (genreFilters.minGenreSequenceCount !== undefined || genreFilters.maxGenreSequenceCount !== undefined) {
      const min = genreFilters.minGenreSequenceCount || 0;
      const max = genreFilters.maxGenreSequenceCount || 999999;
      filterStrings.push(`genre_sequence_count:[${min}..${max}]`);
    }

    // Genre total downloads range
    if (genreFilters.minGenreTotalDownloads !== undefined || genreFilters.maxGenreTotalDownloads !== undefined) {
      const min = genreFilters.minGenreTotalDownloads || 0;
      const max = genreFilters.maxGenreTotalDownloads || 999999;
      filterStrings.push(`genre_total_downloads:[${min}..${max}]`);
    }

    // Genre average rating range
    if (genreFilters.minGenreAverageRating !== undefined || genreFilters.maxGenreAverageRating !== undefined) {
      const min = genreFilters.minGenreAverageRating || 0;
      const max = genreFilters.maxGenreAverageRating || 5;
      filterStrings.push(`genre_average_rating:[${min}..${max}]`);
    }

    // Genre mapping status filter
    if (genreFilters.genreMappingStatus && genreFilters.genreMappingStatus.length > 0) {
      filterStrings.push(`genre_mapping_status:[${genreFilters.genreMappingStatus.map(status => `"${status}"`).join(',')}]`);
    }

    return filterStrings;
  }

  /**
   * Search displays collection - with full filter support
   */
  static async searchDisplays(params: SearchParams) {
    try {
      const searchParams: any = {
        q: params.query || '*',
        query_by: 'display_name,description,city,state,country',
        num_typos: '1,1,1,1,1',
        sort_by: params.sort_by || (params.trendingSort ? 'hot_score:desc,_text_match:desc' : params.upvoteSort ? 'upvote_count:desc,_text_match:desc' : 'created_at:desc'),
        page: params.page || 1,
        per_page: params.per_page || PAGINATION.SEARCH_PAGE_SIZE,
        ...(params.facet_by && { facet_by: params.facet_by.join(',') }),
        include_facet_counts: params.include_facet_counts || true,
        max_facet_values: 100,
      };

      // Location search with geopoint support
      if (params.location && params.radius_km) {
        searchParams.filter_by = `location:(${params.location.lat}, ${params.location.lng}, ${params.radius_km} km)`;
      }

      // Add other filters if provided
      if (params.filters && Object.keys(params.filters).length > 0) {
        const filterStrings = [];
        if (searchParams.filter_by) {
          filterStrings.push(searchParams.filter_by);
        }
        
        // Field mapping for displays_v2 schema
        const fieldMapping: Record<string, string> = {
          // Core fields
          city: 'city',
          state: 'state',
          country: 'country',
          featured: 'featured',
          status: 'status',

          // Holiday types (array field)
          holidays: 'holiday_type',

          // Engagement fields
          min_upvotes: 'upvote_count',
          max_upvotes: 'upvote_count',

          // Date fields
          created_at: 'created_at',
          updated_at: 'updated_at',
        };
        
        for (const [key, value] of Object.entries(params.filters)) {
          if (value === undefined || value === null || value === '' || value === false) {
            continue;
          }
          
          const fieldName = fieldMapping[key] || key;
          
          if (Array.isArray(value)) {
            const nonEmptyValues = value.filter(v => v && typeof v === 'string' && v.trim() !== '');
            if (nonEmptyValues.length > 0) {
              filterStrings.push(`${fieldName}:[${nonEmptyValues.map(v => `"${v}"`).join(',')}]`);
            }
          } else if (typeof value === 'string') {
            if (value.trim() !== '') {
              filterStrings.push(`${fieldName}:"${value}"`);
            }
          } else if (typeof value === 'boolean' && value === true) {
            filterStrings.push(`${fieldName}:${value}`);
          } else if (typeof value === 'number') {
            // Handle numeric ranges
            if (key === 'min_rating' || key === 'min_upvotes' || key === 'min_views') {
              filterStrings.push(`${fieldName}:>=${value}`);
            } else if (key === 'max_rating' || key === 'max_upvotes' || key === 'max_views') {
              filterStrings.push(`${fieldName}:<=${value}`);
            } else {
              filterStrings.push(`${fieldName}:${value}`);
            }
          }
        }
        
        if (filterStrings.length > 0) {
          searchParams.filter_by = filterStrings.join(' && ');
        }
      }

      // Default facets for displays_v2 schema
      if (!searchParams.facet_by) {
        searchParams.facet_by = 'city,state,country,featured,holiday_type,display_type';
      }

      // Use displays_v2 collection with geopoint support
      const result = await typesenseSearchClient.collections('displays_v2').documents().search(searchParams);
      return result;
    } catch (error) {
      console.error('Displays search failed:', error);
      throw new Error(`Displays search failed: ${error.message}`);
    }
  }

  /**
   * Search display_songs collection
   */
  static async searchDisplaySongs(params: SearchParams) {
    try {
      const searchParams = {
        q: params.query || '*',
        query_by: 'song_title,genre,album',
        num_typos: '1,1,0', // Fixed: correct count for query_by fields
        sort_by: params.sort_by || SEARCH.DEFAULT_SORT,
        page: params.page || 1,
        per_page: params.per_page || PAGINATION.SEARCH_PAGE_SIZE,
        ...(params.facet_by && { facet_by: params.facet_by.join(',') }),
        include_facet_counts: params.include_facet_counts || true,
      };

      // Add filters if provided
      if (params.filters && Object.keys(params.filters).length > 0) {
        const filterStrings = [];
        for (const [key, value] of Object.entries(params.filters)) {
          if (Array.isArray(value) && value.length > 0) {
            filterStrings.push(`${key}:[${value.map(v => `"${v}"`).join(',')}]`);
          } else if (value !== undefined && value !== null) {
            filterStrings.push(`${key}:"${value}"`);
          }
        }
        if (filterStrings.length > 0) {
          searchParams.filter_by = filterStrings.join(' && ');
        }
      }

      const result = await typesenseSearchClient.collections('display_songs').documents().search(searchParams);
      return result;
    } catch (error) {
      console.error('Display songs search failed:', error);
      throw new Error(`Display songs search failed: ${error.message}`);
    }
  }

  /**
   * Get search suggestions
   */
  static async getSearchSuggestions(query: string, collection = 'sequences') {
    try {
      const result = await typesenseSearchClient.collections(collection).documents().search({
        q: query,
        query_by: collection === 'sequences' ? 'search_text' : 'display_name',
        per_page: 5,
        facet_by: collection === 'sequences' ? 'genres' : 'city,state',
        max_facet_values: 5,
      });

      return result;
    } catch (error) {
      console.error('Suggestions search failed:', error);
      return { hits: [], facet_counts: [] };
    }
  }

  /**
   * Get genre recommendations based on current search context
   */
  static async getGenreRecommendations(params: {
    currentGenreIds?: string[];
    currentGenreCategories?: string[];
    limit?: number;
    includePopular?: boolean;
    includeRelated?: boolean;
  }) {
    try {
      const { currentGenreIds = [], currentGenreCategories = [], limit = 10, includePopular = true, includeRelated = true } = params;
      
      // Use the new GenreRecommendationService
      const recommendations = await GenreRecommendationService.getRecommendations({
        currentGenreIds,
        currentGenreCategories,
        limit
      });

      // Return only requested recommendation types
      const result = {
        popular: includePopular ? recommendations.popular : [],
        related: includeRelated ? recommendations.related : [],
        featured: recommendations.featured,
        trending: recommendations.trending,
        crossCategory: recommendations.crossCategory
      };

      return result;
    } catch (error) {
      console.error('Genre recommendations failed:', error);
      throw new Error(`Genre recommendations failed: ${error.message}`);
    }
  }

  /**
   * Get personalized genre recommendations for a user
   */
  static async getPersonalizedGenreRecommendations(params: {
    userId: string;
    currentGenreIds?: string[];
    currentGenreCategories?: string[];
    limit?: number;
  }) {
    try {
      const { userId, currentGenreIds = [], currentGenreCategories = [], limit = 10 } = params;
      
      const recommendations = await GenreRecommendationService.getPersonalizedRecommendations(userId, {
        currentGenreIds,
        currentGenreCategories,
        limit
      });

      return recommendations;
    } catch (error) {
      console.error('Personalized genre recommendations failed:', error);
      throw new Error(`Personalized genre recommendations failed: ${error.message}`);
    }
  }

  /**
   * Get search-based genre recommendations
   */
  static async getSearchBasedGenreRecommendations(params: {
    searchQuery: string;
    limit?: number;
  }) {
    try {
      const { searchQuery, limit = 10 } = params;
      
      const recommendations = await GenreRecommendationService.getSearchBasedRecommendations(searchQuery, limit);

      return recommendations;
    } catch (error) {
      console.error('Search-based genre recommendations failed:', error);
      throw new Error(`Search-based genre recommendations failed: ${error.message}`);
    }
  }

  /**
   * Health check
   */
  static async healthCheck() {
    try {
      const sequencesResult = await this.searchSequences({ 
        query: 'christmas', 
        per_page: 1 
      });
      
      return {
        status: 'healthy',
        sequencesCount: sequencesResult.found || 0,
        message: 'Typesense search is operational'
      };
    } catch (error) {
      console.error('Typesense health check failed:', error);
      return {
        status: 'unhealthy',
        error: error.message,
        message: 'Typesense search is not responding'
      };
    }
  }

  /**
   * Search displays within a specific geographic area (radius search)
   */
  static async searchDisplaysByLocation(
    lat: number,
    lng: number,
    radiusKm: number,
    query?: string,
    filters?: Record<string, any>
  ) {
    return this.searchDisplays({
      query: query || '*',
      location: { lat, lng },
      radius_km: radiusKm,
      filters,
      facet_by: ['city', 'state', 'holiday_type', 'display_type'],
      include_facet_counts: true,
    });
  }

  /**
   * Search displays within a bounding box (for map viewport)
   */
  static async searchDisplaysInBounds(
    northEast: { lat: number; lng: number },
    southWest: { lat: number; lng: number },
    query?: string,
    filters?: Record<string, any>
  ) {
    // Calculate center and radius for the bounding box
    const centerLat = (northEast.lat + southWest.lat) / 2;
    const centerLng = (northEast.lng + southWest.lng) / 2;

    // Approximate radius to cover the bounding box
    const latDiff = Math.abs(northEast.lat - southWest.lat);
    const lngDiff = Math.abs(northEast.lng - southWest.lng);
    const radiusKm = Math.max(latDiff, lngDiff) * 111; // rough km per degree

    return this.searchDisplaysByLocation(centerLat, centerLng, radiusKm, query, filters);
  }

  /**
   * Get displays near a specific location for map clustering
   */
  static async getDisplaysForMapClustering(
    centerLat: number,
    centerLng: number,
    radiusKm: number,
    maxResults: number = 1000
  ) {
    return this.searchDisplays({
      query: '*',
      location: { lat: centerLat, lng: centerLng },
      radius_km: radiusKm,
      per_page: maxResults,
      sort_by: 'upvote_count:desc',
      filters: { status: 'active' },
    });
  }
} 